import React, { useEffect, useRef, useState } from "react";

export function getActiveGroupJobs(jobs = [], activeSessions = {}) {
  const groups = new Map();

  for (const job of jobs) {
    const session = activeSessions[job.id];
    const groupId = session?.sessionGroupId;
    const state = session?.state;
    if (!groupId) continue;
    if (state !== "running" && state !== "paused") continue;

    if (!groups.has(groupId)) {
      groups.set(groupId, []);
    }
    groups.get(groupId).push(job);
  }

  const groupList = [...groups.values()].filter((group) => group.length > 1);
  if (groupList.length === 0) return [];
  if (groupList.length === 1) return groupList[0];
  return groupList.flat();
}

export default function JobTracker({
  jobs = [],
  selectedOrder,
  selectedOperation,
  selectedJob,
  onJobClick,
  onStartJob,
  onStartGroupJobs,
  onInstantComplete,
  onCompleteGroupJobs,
  activeSessions = {},
  onPauseJob,
  onResumeJob,
  onCompleteJob,
  selectedJobIds = [],
  onSelectionChange,
}) {
  // Check if this is a PM operation (instant-complete instead of timer)
  const isPMOperation = selectedOperation?.workCenter === "PM";

  const [maxHeight, setMaxHeight] = useState(480);
  const selectAllRef = useRef(null);

  const selectedIds = selectedJobIds || [];

  // Count selected jobs that can be started/completed (no active session)
  const startableSelectedJobs = jobs.filter((job) => {
    if (!selectedIds.includes(job.id)) return false;
    const session = activeSessions[job.id];
    const hasSession = !!session && (session.state === "running" || session.state === "paused");
    return !hasSession;
  });

  const activeGroupJobs = getActiveGroupJobs(jobs, activeSessions);
  const showCompleteAllBar = activeGroupJobs.length > 1;
  const canStartGroup = startableSelectedJobs.length > 1 && !isPMOperation;
  const canCompleteGroupPM = startableSelectedJobs.length > 1 && isPMOperation;
  const hasSelectionBar = canStartGroup || canCompleteGroupPM;
  const actionBarCount = (showCompleteAllBar ? 1 : 0) + (hasSelectionBar ? 1 : 0);
  const headerTopClass = actionBarCount === 0
    ? "top-0"
    : actionBarCount === 1
      ? "top-[52px]"
      : "top-[104px]";

  function toggleJobSelection(jobId) {
    if (!onSelectionChange) return;
    if (selectedIds.includes(jobId)) {
      onSelectionChange(selectedIds.filter((id) => id !== jobId));
    } else {
      onSelectionChange([...selectedIds, jobId]);
    }
  }

  function handleSelectAll() {
    if (!onSelectionChange) return;

    // Only include jobs without active sessions
    const selectableJobs = jobs.filter((job) => {
      const session = activeSessions[job.id];
      const hasSession = !!session && (session.state === "running" || session.state === "paused");
      return !hasSession;
    });

    if (selectedIds.length === selectableJobs.length && selectableJobs.length > 0) {
      onSelectionChange([]);
    } else {
      onSelectionChange(selectableJobs.map((job) => job.id));
    }
  }

  useEffect(() => {
    function updateHeight() {
      const h = Math.min(480, window.innerHeight - 420);
      setMaxHeight(h);
    }
    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  useEffect(() => {
    if (!selectAllRef.current) return;

    if (selectedIds.length === 0) {
      selectAllRef.current.indeterminate = false;
      selectAllRef.current.checked = false;
    } else if (selectedIds.length === jobs.length) {
      selectAllRef.current.indeterminate = false;
      selectAllRef.current.checked = true;
    } else {
      selectAllRef.current.indeterminate = true;
    }
  }, [selectedIds, jobs.length]);

  if (!selectedOrder) {
    return (
      <div className="text-xs text-gray-500">
        Select an order first to see jobs.
      </div>
    );
  }

  if (!jobs.length) {
    return (
      <div className="text-xs text-gray-500">
        No jobs found for this order.
      </div>
    );
  }

  const operationLabel =
    selectedOperation?.operationCode ||
    selectedOperation?.operation ||
    selectedOperation?.operationName ||
    "—";

  function broadcastJobContext(job) {
    if (!job) return;

    try {
      window.dispatchEvent(
        new CustomEvent("job-context", {
          detail: {
            jobId: job.id,
            jobNumber: job.jobNumber,
            jobName: job.name,
            orderNumber: selectedOrder?.orderNumber ?? null,
            quoteNumber: selectedOrder?.quoteNumber ?? null,
            projectName: selectedOrder?.projectName ?? null,
            quantityToMake: job.qty,
            productCode: job.productCode,
            qty: job.qty,
          },
        })
      );
    } catch (err) {
      console.error("Error broadcasting job-context:", err);
    }
  }

  return (
    <div
      style={{ maxHeight }}
      className="
        overflow-y-auto border border-gray-200 rounded-lg
        bg-white divide-y divide-gray-100
      "
    >
      {/* GROUP ACTION BAR - shows when multiple jobs selected */}
      {showCompleteAllBar && (
        <div
          className="px-3 py-2 bg-emerald-50 border-b border-emerald-200 flex items-center justify-between sticky z-20"
          style={{ top: 0 }}
        >
          <span className="text-sm text-emerald-700">
            {activeGroupJobs.length} active group jobs
          </span>
          <button
            type="button"
            className="
              bg-emerald-600 hover:bg-emerald-700
              text-white text-sm font-medium px-4 py-1.5 rounded shadow-md
              flex items-center gap-2 transition-all
            "
            onClick={(e) => {
              e.stopPropagation();
              if (onCompleteGroupJobs) {
                onCompleteGroupJobs(activeGroupJobs);
              }
            }}
          >
            Complete All ({activeGroupJobs.length})
          </button>
        </div>
      )}

      {canStartGroup && (
        <div
          className="px-3 py-2 bg-purple-50 border-b border-purple-200 flex items-center justify-between sticky z-20"
          style={{ top: showCompleteAllBar ? 52 : 0 }}
        >
          <span className="text-sm text-purple-700">
            {startableSelectedJobs.length} jobs selected
          </span>
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (onStartGroupJobs) {
                onStartGroupJobs(startableSelectedJobs);
              }
            }}
            className="
              bg-gradient-to-r from-purple-600 to-orange-500 hover:from-purple-700 hover:to-orange-600
              text-white text-sm font-medium px-4 py-1.5 rounded shadow-md
              flex items-center gap-2 transition-all
            "
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
            </svg>
            Start {startableSelectedJobs.length} Jobs
          </button>
        </div>
      )}

      {/* GROUP COMPLETE BAR - shows when multiple jobs selected for PM operations */}
      {canCompleteGroupPM && (
        <div
          className="px-3 py-2 bg-emerald-50 border-b border-emerald-200 flex items-center justify-between sticky z-20"
          style={{ top: showCompleteAllBar ? 52 : 0 }}
        >
          <span className="text-sm text-emerald-700">
            {startableSelectedJobs.length} jobs selected
          </span>
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (onInstantComplete) {
                onInstantComplete(null, startableSelectedJobs);
              }
            }}
            className="
              bg-emerald-600 hover:bg-emerald-700
              text-white text-sm font-medium px-4 py-1.5 rounded shadow-md
              flex items-center gap-2 transition-all
            "
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            Complete {startableSelectedJobs.length} Jobs
          </button>
        </div>
      )}

      {/* HEADER */}
      <div
        className={`
          grid grid-cols-[30px_60px_90px_120px_minmax(0,1fr)_100px_130px_120px]
          text-[11px] font-semibold text-gray-600
          px-3 py-2 bg-slate-200 border-b border-gray-200 sticky z-10
          ${headerTopClass}
        `}
      >
        <div
          className="flex items-center"
          onClick={(e) => e.stopPropagation()}
        >
          <input
            ref={selectAllRef}
            type="checkbox"
            className="h-3 w-3 accent-purple-500"
            onChange={handleSelectAll}
          />
        </div>

        <div>Status</div>
        <div>Job #</div>
        <div>Product Type</div>
        <div>SKU Name</div>
        <div className="text-center">Qty to Make</div>
        <div className="text-purple-600 font-bold">Operation</div>
        <div className="text-right">Time</div>
      </div>

      {/* JOB ROWS */}
      {jobs.map((job) => {
        const isSelectedRow = selectedJob?.id === job.id;
        const isChecked = selectedIds.includes(job.id);

        const session = activeSessions[job.id];
        const state = session?.state;

        const isRunning = state === "running";
        const isPaused = state === "paused";
        const hasSession = !!session && (isRunning || isPaused);

        return (
          <div
            key={job.id}
            onClick={() => onJobClick && onJobClick(job)}
            className={`
              grid grid-cols-[30px_60px_90px_120px_minmax(0,1fr)_100px_130px_120px]
              px-3 py-2 text-sm cursor-pointer transition text-gray-900
              ${
                isSelectedRow
                  ? "bg-purple-50 border-l-2 border-purple-500"
                  : "hover:bg-slate-200"
              }
            `}
          >
            <div
              className="flex items-center"
              onClick={(e) => e.stopPropagation()}
            >
              <input
                type="checkbox"
                className={`h-3 w-3 accent-purple-500 ${hasSession ? 'opacity-30 cursor-not-allowed' : ''}`}
                checked={isChecked}
                disabled={hasSession}
                onChange={() => toggleJobSelection(job.id)}
                title={hasSession ? "Job has an active session" : "Select for group action"}
              />
            </div>

            <div className="text-gray-500 text-xs">{job.status || "—"}</div>

            <div className="font-semibold text-gray-900">{job.jobNumber}</div>

            <div className="text-xs text-gray-600">
              {job.productCode || "—"}
            </div>

            <div
              className="truncate whitespace-nowrap text-gray-700"
              style={{ maxWidth: "40ch" }}
              title={job.name}
            >
              {job.name || "Unnamed Job"}
            </div>

            <div className="text-center text-amber-600 font-semibold">
              {job.qty}
            </div>

            <div className="text-purple-600 font-bold">{operationLabel}</div>

            <div className="flex justify-end items-center gap-1">
              {!hasSession && isPMOperation && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    broadcastJobContext(job);
                    if (onInstantComplete) onInstantComplete(job);
                  }}
                  className="
                    bg-emerald-600 hover:bg-emerald-700 border border-emerald-400
                    text-gray-900 text-xs font-medium px-3 py-1 rounded
                  "
                >
                  COMPLETE
                </button>
              )}

              {!hasSession && !isPMOperation && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    broadcastJobContext(job);
                    if (onStartJob) onStartJob(job);
                  }}
                  className="
                    bg-gradient-to-r from-purple-600 to-orange-500 hover:from-purple-700 hover:to-orange-600
                    text-white text-xs px-3 py-1 rounded shadow-sm transition-all
                  "
                >
                  START
                </button>
              )}

              {isRunning && (
                <>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onPauseJob) onPauseJob(job);
                    }}
                    className="
                      bg-yellow-500 hover:bg-yellow-600
                      text-gray-900 text-[11px] px-2 py-1 rounded
                    "
                  >
                    Pause
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onCompleteJob) onCompleteJob(job);
                    }}
                    className="
                      bg-red-600 hover:bg-red-700
                      text-gray-900 text-[11px] px-2 py-1 rounded
                    "
                  >
                    Complete
                  </button>
                </>
              )}

              {isPaused && (
                <>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onResumeJob) onResumeJob(job);
                    }}
                    className="
                      bg-green-600 hover:bg-green-700
                      text-gray-900 text-[11px] px-2 py-1 rounded
                    "
                  >
                    Resume
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onCompleteJob) onCompleteJob(job);
                    }}
                    className="
                      bg-red-600 hover:bg-red-700
                      text-gray-900 text-[11px] px-2 py-1 rounded
                    "
                  >
                    Complete
                  </button>
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
